import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldCheck, Loader2, Check, Phone } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsersModal() {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [allVend, setAllVend] = useState([]);
  const [selected, setSelected] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [phone, setPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [role, setRole] = useState("analista");
  const [savingRole, setSavingRole] = useState(false);
  const [permGranos, setPermGranos] = useState(false);
  const [savingGranos, setSavingGranos] = useState(false);
  const [canAssignGranos, setCanAssignGranos] = useState(false);
  const [isSuper, setIsSuper] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [u, v, me] = await Promise.all([api.get("/admin/users"), api.get("/admin/vendedores"), api.get("/auth/me")]);
    setUsers(u.data);
    setAllVend(v.data.vendedores || []);
    setCanAssignGranos(!!me.data.can_assign_granos);
    setIsSuper(!!me.data.is_superadmin);
  };

  const loadHistory = async (email) => {
    try {
      const { data } = await api.get("/admin/role-history", { params: email ? { email } : {} });
      setHistory(data.history || []);
      setShowHistory(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el historial");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const pick = (email) => {
    setSelected(email);
    const u = users.find((x) => x.email === email);
    setChecked(new Set(u?.assigned_vendedores || []));
    setPhone(u?.phone || "");
    setRole(u?.role || "analista");
    setPermGranos(!!u?.perm_granos);
  };

  const toggle = (name) => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  };

  const savePhone = async () => {
    if (!selected) return toast.error("Selecciona un usuario");
    setSavingPhone(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(selected)}/phone`, { phone });
      toast.success("Teléfono guardado. El usuario ya puede consultar por WhatsApp.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el teléfono");
    } finally {
      setSavingPhone(false);
    }
  };

  const saveRole = async (newRole) => {
    if (!selected) return;
    setSavingRole(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(selected)}/role`, { role: newRole });
      setRole(newRole);
      toast.success("Nivel de permisos actualizado.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el rol");
    } finally {
      setSavingRole(false);
    }
  };

  const save = async () => {
    if (!selected) return toast.error("Selecciona un usuario");
    setSaving(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(selected)}/vendedores`, { vendedores: [...checked] });
      toast.success("Asignación guardada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveGranos = async (val) => {
    if (!selected) return;
    setSavingGranos(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(selected)}/granos`, { perm_granos: val });
      setPermGranos(val);
      toast.success(val ? "Acceso a Granos habilitado." : "Acceso a Granos quitado.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el acceso a Granos");
    } finally {
      setSavingGranos(false);
    }
  };

  const selectedUser = users.find((x) => x.email === selected);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-permisos-button" variant="outline" className="h-9 border-amber-200 text-amber-800 hover:bg-amber-50">
          <ShieldCheck className="h-4 w-4 mr-1.5" /> Permisos
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl" data-testid="admin-users-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <ShieldCheck className="h-5 w-5" /> Asignación de Vendedores
          </DialogTitle>
          <DialogDescription>
            Selecciona un usuario y marca los vendedores que podrá ver y cargar. Sin asignación, el usuario no verá informes.
          </DialogDescription>
        </DialogHeader>

        {isSuper && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 mb-1" data-testid="admin-role-history-block">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Historial de permisos</span>
              <Button
                data-testid="admin-role-history-btn"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => (showHistory ? setShowHistory(false) : loadHistory(selected || undefined))}
              >
                {showHistory ? "Ocultar" : selected ? "Ver de este usuario" : "Ver todos"}
              </Button>
            </div>
            {showHistory && (
              <div className="max-h-40 overflow-y-auto mt-2 space-y-1" data-testid="admin-role-history-list">
                {history.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2 text-center">Sin cambios de rol registrados.</p>
                ) : (
                  history.map((h, i) => (
                    <div key={i} className="text-[11px] text-slate-600 border-b border-slate-100 py-1">
                      <span className="font-medium text-slate-800">{h.target}</span>: {h.old_role || "—"} → <span className="font-semibold text-amber-700">{h.new_role}</span>
                      <span className="text-slate-400"> · por {h.actor} · {(h.at || "").slice(0, 16).replace("T", " ")} · {h.origin}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Usuarios ({users.length})</p>
            <div className="max-h-72 overflow-y-auto space-y-1" data-testid="admin-users-list">
              {users.map((u) => (
                <button
                  key={u.email}
                  data-testid={`admin-user-${u.email}`}
                  onClick={() => pick(u.email)}
                  className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${selected === u.email ? "border-amber-400 bg-amber-50" : "border-slate-100 hover:bg-slate-50"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{u.name || u.email}</span>
                    {u.role === "admin"
                      ? <span className="text-[10px] font-bold uppercase text-amber-600">admin</span>
                      : u.role === "coordinador"
                      ? <span className="text-[10px] font-bold uppercase text-emerald-600">coord</span>
                      : <span className="text-[10px] text-slate-400">{(u.assigned_vendedores || []).length} asig.</span>}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            {selected && (
              <div className="mb-4 rounded-md border border-amber-100 bg-amber-50/40 p-3" data-testid="admin-user-role-block">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1.5 flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Nivel de permisos
                </p>
                <div className="flex gap-1.5">
                  {[["admin", "Administrador"], ["coordinador", "Coordinador"], ["analista", "Vendedor"]].map(([val, lab]) => (
                    <button
                      key={val}
                      data-testid={`admin-role-${val}`}
                      onClick={() => isSuper && saveRole(val)}
                      disabled={savingRole || !isSuper}
                      className={`flex-1 text-xs font-semibold rounded-md border px-2 py-1.5 transition-colors ${role === val ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-amber-50"} ${!isSuper ? "cursor-not-allowed opacity-80" : ""}`}
                    >
                      {lab}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5" data-testid="admin-role-help">
                  {!isSuper
                    ? "🔒 Solo el superadministrador puede cambiar el nivel de permisos. Vista de solo lectura."
                    : role === "admin"
                    ? "Acceso total a toda la cartera y todos los vendedores."
                    : role === "coordinador"
                    ? "Consulta la cartera completa de todos los vendedores que gestiona."
                    : "Sólo los clientes de los vendedores directamente asignados."}
                </p>
              </div>
            )}
            {selected && (
              <div className="mb-4 rounded-md border border-lime-200 bg-lime-50/50 p-3" data-testid="admin-user-granos-block">
                <p className="text-xs font-semibold uppercase tracking-wide text-lime-800 mb-1.5 flex items-center gap-1.5">
                  🌾 Acceso a Granos (WhatsApp)
                </p>
                {canAssignGranos ? (
                  <div className="flex gap-1.5">
                    {[[true, "Habilitado"], [false, "Sin acceso"]].map(([val, lab]) => (
                      <button
                        key={String(val)}
                        data-testid={`admin-granos-${val ? "on" : "off"}`}
                        onClick={() => saveGranos(val)}
                        disabled={savingGranos}
                        className={`flex-1 text-xs font-semibold rounded-md border px-2 py-1.5 transition-colors ${permGranos === val ? (val ? "bg-[#0f5233] border-[#0f5233] text-white" : "bg-slate-500 border-slate-500 text-white") : "bg-white border-slate-200 text-slate-600 hover:bg-lime-50"}`}
                      >
                        {lab}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500">
                    {permGranos ? "✅ Este usuario tiene acceso a Granos." : "Sin acceso a Granos."} Solo el administrador principal puede modificarlo.
                  </p>
                )}
              </div>
            )}
            {selected && (
              <div className="mb-4 rounded-md border border-emerald-100 bg-emerald-50/40 p-3" data-testid="admin-user-phone-block">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1.5 flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" /> Teléfono WhatsApp
                </p>
                <div className="flex gap-2">
                  <Input
                    data-testid="admin-user-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+595 981 000000"
                    className="h-9 bg-white"
                  />
                  <Button
                    data-testid="admin-save-phone"
                    onClick={savePhone}
                    disabled={savingPhone}
                    className="h-9 bg-emerald-700 hover:bg-emerald-800 shrink-0"
                  >
                    {savingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">Habilita las consultas de este usuario por el chatbot de WhatsApp.</p>
              </div>
            )}
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Vendedores {selected && selectedUser?.role !== "admin" ? `· ${checked.size} seleccionados` : ""}
            </p>
            {!selected ? (
              <p className="text-sm text-slate-400 py-6 text-center">Selecciona un usuario para asignar vendedores.</p>
            ) : selectedUser?.role === "admin" ? (
              <p className="text-sm text-emerald-700 py-6 text-center">Los administradores ya tienen acceso a todos los vendedores.</p>
            ) : (
              <>
                <div className="max-h-64 overflow-y-auto space-y-1 rounded-md border border-slate-100 p-2" data-testid="admin-vendedores-checklist">
                  {allVend.length === 0 ? (
                    <p className="text-sm text-slate-400 py-3 text-center">No hay vendedores en el sistema.</p>
                  ) : (
                    allVend.map((v) => (
                      <label key={v} data-testid={`admin-vend-check-${v}`} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked.has(v) ? "bg-emerald-600 border-emerald-600" : "border-slate-300"}`}>
                          {checked.has(v) && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <input type="checkbox" className="sr-only" checked={checked.has(v)} onChange={() => toggle(v)} />
                        <span className="text-sm text-slate-700 truncate">{v}</span>
                      </label>
                    ))
                  )}
                </div>
                <Button data-testid="admin-save-assign" onClick={save} disabled={saving} className="w-full mt-3 bg-amber-600 hover:bg-amber-700">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
                  Guardar asignación
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
